import { logger } from "./logger";

const GRAPH_BASE_URL = "https://graph.microsoft.com/v1.0";

/** Reads the OneDrive driveItem id from a document (casing varies by list). */
function getDriveItemId(file) {
  return file?.id ?? file?.Id ?? file?.key ?? file?.OneDriveDocId ?? "";
}

/** Reads the OneDrive drive id from a document (casing varies by list). */
function getDriveId(file) {
  return file?.DriveId ?? file?.driveId ?? file?.OneDriveDriveId ?? "";
}

/** Reads the browser/viewer link from a document (field name varies by list). */
function getWebUrl(file) {
  return (
    file?.webUrl ??
    file?.WebUrl ??
    file?.weblink ??
    file?.webLinkUrl ??
    ""
  );
}

/**
 * True when the link returned by createOneDriveShareLink is a real sharing
 * link/invite, not the plain webUrl fallback (which means nothing was shared).
 */
export function isNewSharingLink(file, link) {
  return Boolean(link) && link !== getWebUrl(file);
}

/** Best-effort human-readable message from a failed Graph response. */
async function readGraphError(response) {
  try {
    const data = await response.json();
    return data?.error?.message || "";
  } catch (_err) {
    return "";
  }
}

/** Encodes a sharing URL into the "u!"-prefixed token the Graph /shares API expects. */
function encodeSharingUrl(url) {
  const base64 = window.btoa(unescape(encodeURIComponent(String(url))));
  const base64url = base64.replace(/=+$/, "").replace(/\//g, "_").replace(/\+/g, "-");
  return `u!${base64url}`;
}

/**
 * Resolves the {driveId, itemId} for a document. Prefers ids already on the file;
 * when the drive id is missing (the list DTO often omits it), it resolves the
 * driveItem from its webUrl via the Graph /shares endpoint.
 */
async function resolveDriveItemRef(accessToken, file) {
  let driveId = getDriveId(file);
  let itemId = getDriveItemId(file);
  if (driveId && itemId) return { driveId, itemId };

  const webUrl = getWebUrl(file);
  if (!accessToken || !webUrl) return { driveId, itemId };

  try {
    const response = await fetch(
      `${GRAPH_BASE_URL}/shares/${encodeSharingUrl(webUrl)}/driveItem?$select=id,parentReference`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    if (!response.ok) {
      logger.warn(
        "resolveDriveItemRef /shares lookup failed",
        { status: response.status },
        "SHARE",
      );
      return { driveId, itemId };
    }
    const data = await response.json();
    return {
      driveId: data?.parentReference?.driveId || driveId,
      itemId: data?.id || itemId,
    };
  } catch (error) {
    logger.warn("resolveDriveItemRef threw", { message: error?.message }, "SHARE");
    return { driveId, itemId };
  }
}

/**
 * Generates a shareable link for a OneDrive/channel document via Microsoft Graph.
 *
 * - With no `recipients`, mints an organization-scoped view link (`createLink`);
 *   if that is not possible (missing ids, insufficient scope, or any Graph error)
 *   it falls back to the item's `webUrl` so the user still gets a usable link.
 * - With `recipients` (emails) and no `sendInvitation`, mints a "People you
 *   specify" link (`createLink`, scope "users") for the user to copy.
 * - With `recipients` and `sendInvitation`, grants access via the `invite`
 *   action, which emails the recipients.
 * - Re-sharing only adds access: an earlier "Can edit" share is kept alongside a
 *   new "Can view" one. Lowering access is done from Sharing Details.
 *   Recipient failures throw so the caller can surface a real error instead of
 *   copying a link nobody new can use.
 *
 * @param {string} accessToken A Microsoft Graph bearer token.
 * @param {object} file The document (needs id + DriveId; webUrl used as fallback).
 * @param {{ recipients?: Array<{ email: string }>, role?: "read"|"write",
 *           sendInvitation?: boolean, message?: string }} [options]
 * @returns {Promise<string>} The sharing link, or "" when none could be resolved.
 */
export async function createOneDriveShareLink(accessToken, file, options = {}) {
  const recipients = Array.isArray(options.recipients) ? options.recipients : null;
  const restrictToUsers = Boolean(recipients && recipients.length);
  const role = options.role === "write" ? "write" : "read";
  const sendInvitation = Boolean(options.sendInvitation);
  const message = typeof options.message === "string" ? options.message : "";

  const fallbackUrl = getWebUrl(file);
  const { driveId, itemId } = await resolveDriveItemRef(accessToken, file);

  if (!accessToken || !itemId || !driveId) {
    if (restrictToUsers) {
      throw new Error(
        "This document is missing the identifiers needed to share it with specific people.",
      );
    }
    return fallbackUrl;
  }

  const itemBase = `${GRAPH_BASE_URL}/drives/${encodeURIComponent(
    driveId,
  )}/items/${encodeURIComponent(itemId)}`;

  // Copy link for specific people: mint a "People you specify" sharing link
  // (createLink, scope "users"), the same thing OneDrive's own Copy link does.
  // Unlike `invite` with sendInvitation:false, this works for external
  // recipients who aren't yet guests in the tenant — `invite` can't grant them
  // direct access without emailing them, and fails with `sharingFailed`.
  if (restrictToUsers && !sendInvitation) {
    const response = await fetch(`${itemBase}/createLink`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        type: role === "write" ? "edit" : "view",
        scope: "users",
        recipients,
      }),
    });

    if (!response.ok) {
      const detail = await readGraphError(response);
      logger.warn(
        "createOneDriveShareLink createLink (users) failed",
        { status: response.status, fileId: itemId, detail },
        "SHARE",
      );
      throw new Error(detail || `Could not share the document (${response.status}).`);
    }

    const data = await response.json();
    return data?.link?.webUrl || fallbackUrl;
  }

  // Send to specific people: grant them access via the `invite` action and let
  // Graph email the invitation (this also creates guest access for externals).
  if (restrictToUsers) {
    const response = await fetch(`${itemBase}/invite`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        recipients,
        message,
        requireSignIn: true,
        sendInvitation,
        roles: [role],
      }),
    });

    if (!response.ok) {
      const detail = await readGraphError(response);
      logger.warn(
        "createOneDriveShareLink invite failed",
        { status: response.status, fileId: itemId, detail },
        "SHARE",
      );
      throw new Error(detail || `Could not share the document (${response.status}).`);
    }

    const data = await response.json();
    return data?.value?.[0]?.link?.webUrl || fallbackUrl;
  }

  // Organization-wide link: mint an org-scoped view link, falling back to webUrl.
  try {
    const response = await fetch(`${itemBase}/createLink`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        type: role === "write" ? "edit" : "view",
        scope: "organization",
      }),
    });

    if (!response.ok) {
      const detail = await readGraphError(response);
      logger.warn(
        "createOneDriveShareLink createLink failed",
        { status: response.status, fileId: itemId, detail },
        "SHARE",
      );
      return fallbackUrl;
    }

    const data = await response.json();
    return data?.link?.webUrl || fallbackUrl;
  } catch (error) {
    logger.warn(
      "createOneDriveShareLink threw",
      { message: error?.message, fileId: itemId },
      "SHARE",
    );
    return fallbackUrl;
  }
}
